import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema, generateArticleSchema } from "@/lib/jsonld";

import coverImage from "@/assets/blog/client-ambassadeur/couverture.webp";
import conditionsImage from "@/assets/blog/client-ambassadeur/conditions-client-ambassadeur-fidelisation.webp";
import parrainageImage from "@/assets/blog/client-ambassadeur/parrainage-client-qr-code-wallet.webp";
import kpiImage from "@/assets/blog/client-ambassadeur/kpi-ambassadeurs-parrainage-performance.webp";

const ClientAmbassadeur = () => {
  const jsonLdSchemas = [
    generateWebPageSchema(
      "Comment transformer un client satisfait en client ambassadeur",
      seoConfig.blogClientAmbassadeur.description,
      "/client-ambassadeur"
    ),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: "Client ambassadeur", path: "/client-ambassadeur" }
    ]),
    generateArticleSchema(
      "Comment transformer un client satisfait en client ambassadeur",
      seoConfig.blogClientAmbassadeur.description,
      "https://unotif.com/client-ambassadeur",
      "2026-04-16"
    )
  ];

  const imageStyle = {
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blogClientAmbassadeur} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-sm font-medium text-[#495456] mb-6">
              <Link to="/blog" className="text-[#0c97a9] hover:underline">Blog</Link>
              <span className="mx-2">›</span>
              <span className="text-[#074155]">Client ambassadeur</span>
            </nav>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-8">
              Comment transformer un client satisfait en client ambassadeur
            </h1>

            <img
              src={coverImage}
              alt="Comment transformer un client satisfait en client ambassadeur avec stratégie de recommandation et fidélisation"
              loading="eager"
              className="rounded-2xl w-full h-auto"
              style={imageStyle}
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Introduction */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un client satisfait est un actif silencieux. Il revient, il paye, il ne se plaint pas. Un <strong className="font-semibold">client ambassadeur</strong> est un actif actif : il parle de vous à son entourage, vous recommande sans qu'on le lui demande, et ramène des clients que vous n'auriez jamais touchés avec un budget publicitaire.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La différence entre les deux n'est pas une question de satisfaction. La majorité des clients satisfaits ne recommandent jamais spontanément. C'est une question de conditions créées. Cet article explique pourquoi certains clients passent naturellement de "contents" à "actifs dans la recommandation", et quels mécanismes pratiques vous pouvez mettre en place pour accélérer ce passage. Pour poser les bases de votre stratégie avant d'aller plus loin, notre guide sur <Link to="/fidelisation-client-guide" className="text-[#0c97a9] hover:underline">comment fidéliser ses clients</Link> couvre le sujet de A à Z.
            </p>

            {/* Section 1 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              La différence entre satisfaction et engagement actif
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un client satisfait a eu une expérience positive : il n'a pas de raison de partir, mais il n'a pas non plus de raison de parler. La satisfaction est neutre sur le plan de la recommandation. Le silence après une bonne expérience est la norme, pas l'exception.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'ambassadeur naît d'une expérience remarquable : quelque chose qui dépasse les attentes de façon mémorable, qui crée une rupture dans l'ordinaire. Le Net Promoter Score (NPS) mesure exactement cet écart. Les clients qui notent leur expérience 9 ou 10 sur 10 (les "Promoteurs") génèrent un <strong className="font-semibold">bouche-à-oreille</strong> actif. Ceux qui notent 7 ou 8 (les "Passifs") sont des clients fidèles, mais silencieux.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un promoteur NPS génère en moyenne 2 à 5 nouvelles personnes contactées par an, avec un taux de conversion 4 fois supérieur à la publicité standard (Bain & Company). L'ambassadeur est votre canal d'acquisition le moins cher et le plus crédible. La question à se poser : qu'est-ce qui rendrait votre client incapable de ne pas parler de vous ?
            </p>

            {/* Section 2 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Les 4 conditions qui font naître un ambassadeur spontané
            </h2>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              Une expérience qui dépasse une promesse explicite
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'<strong className="font-semibold">ambassadeur client</strong> ne naît pas d'un "comme prévu". Il naît d'un "je ne m'y attendais pas". Le commerçant qui se souvient d'un détail personnel lors d'une visite suivante, le prestataire qui livre 24 heures avant l'heure promise, le café qui glisse un geste inattendu le jour d'un anniversaire : ce n'est pas l'acte lui-même qui crée l'ambassadeur, c'est la rupture avec l'ordinaire.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ce type d'expérience déclenche une envie naturelle de partager. Pas parce que le client veut faire votre publicité, mais parce qu'il veut que ses proches bénéficient de la même attention. C'est un réflexe humain, pas une démarche marketing.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              Un sentiment de reconnaissance individuelle
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le client qui se sent vu parle de vous parce qu'il veut que les autres vivent la même chose. Un message personnalisé qui fait référence à son dernier achat, un prénom utilisé au bon moment, une offre construite sur son profil spécifique : ces gestes construisent un lien que les remises ne créent pas.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La reconnaissance est encore plus puissante quand elle arrive entre deux visites. Un message envoyé via <strong className="font-semibold">notification push wallet</strong> qui dit "vous êtes chez nous depuis 6 mois, voici quelque chose pour vous" a un impact émotionnel sans commune mesure avec une newsletter générique.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              Un sentiment d'appartenance à quelque chose de distinct
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les ambassadeurs les plus actifs se sentent membres d'une communauté, pas seulement clients d'un commerce. L'accès anticipé à une nouveauté réservé aux membres, une vente privée exclusive, un statut visible ("membre historique", "client premium") : ces formats créent un sentiment d'appartenance qui donne au client quelque chose de concret à partager.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le mécanisme est simple : "je fais partie d'un cercle que tu n'as pas encore rejoint." Cette phrase implicite déclenche plus de recommandations que n'importe quelle campagne de parrainage traditionnelle.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              Une occasion facile de recommander
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              Beaucoup de clients satisfaits ne recommandent pas, faute d'occasion ou faute d'outil simple. Si recommander prend 5 minutes de recherche, même un client enthousiaste ne le fait pas. Le mécanisme de <strong className="font-semibold">parrainage</strong> doit être accessible en 30 secondes : un lien, un QR code, un message pré-rédigé à transférer. Si transmettre la recommandation est fluide, elle se produit. Si elle demande un effort, elle ne se produit pas.
            </p>

            <img
              src={conditionsImage}
              alt="Schéma des conditions pour transformer un client en ambassadeur avec expérience, reconnaissance et appartenance"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Section 3 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Identifier ses clients ambassadeurs potentiels
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Vous n'avez pas besoin d'un outil sophistiqué pour repérer vos ambassadeurs potentiels. Un tableau simple suffit pour démarrer.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse rounded-xl overflow-hidden text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold">Signal</th>
                    <th className="px-4 py-3 text-left font-semibold">Interprétation</th>
                    <th className="px-4 py-3 text-left font-semibold">Action suggérée</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Plus de 8 visites sur 6 mois</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Client régulier ancré</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Proposer un statut membre premium</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Partage spontané sur les réseaux</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Ambassadeur actif potentiel</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Offrir accès anticipé ou contenu exclusif</td>
                  </tr>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Panier moyen supérieur à la moyenne</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Client à haute valeur</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Programme parrainage personnalisé</td>
                  </tr>
                  <tr className="bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Jamais de plainte, toujours présent</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Passif satisfait</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Créer une expérience remarquable pour déclencher l'attachement</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Une question directe révèle aussi le niveau d'attachement : "Si vous deviez recommander notre commerce à quelqu'un que vous connaissez, comment le décririez-vous ?" La réponse vous donne à la fois le niveau de fidélité émotionnelle et le message que votre client porte naturellement.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Dans un programme wallet, les clients qui ouvrent les notifications régulièrement, cumulent des points sans les utiliser immédiatement, et reviennent dans les 7 jours après une communication sont vos ambassadeurs dormants. Ils n'attendent qu'un mécanisme pour s'activer.
            </p>

            {/* Section 4 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Les mécanismes concrets pour activer le bouche-à-oreille
            </h2>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              Le programme de parrainage simple
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un <strong className="font-semibold">programme ambassadeur</strong> repose sur un principe : le client reçoit un avantage pour chaque nouveau client qu'il amène. La clé est la simplicité du partage. Un QR code sur la carte wallet suffit : le filleul scanne, s'inscrit, et le parrain est crédité automatiquement.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Deux mécaniques fonctionnent bien selon votre objectif. La récompense immédiate dès l'inscription du filleul (sans qu'il ait besoin d'acheter) maximise le volume de parrainages. La récompense après le premier achat du filleul est plus qualitative : elle génère moins de parrainages mais des filleuls mieux convertis.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              Le contenu à partager
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Donnez à vos clients quelque chose de concret à transmettre : une offre de bienvenue pour leur entourage, un code de réduction unique lié à leur profil, un message pré-rédigé à transférer. La <strong className="font-semibold">notification push wallet</strong> est le canal idéal pour distribuer ce contenu directement sur le téléphone, avec un lien de parrainage accessible en un tap.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le client n'a pas à chercher le lien, à se souvenir d'un code, à aller sur votre site. Il reçoit le contenu sur son téléphone et le transfère en 10 secondes.
            </p>

            <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3 mt-8">
              L'événement exclusif membres
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Une vente privée réservée aux membres, un avant-goût d'une nouveauté, un événement "portes fermées" : ces formats créent naturellement du bouche-à-oreille. Le client qui y participe en parle, précisément parce que son entourage n'y a pas accès. L'exclusivité est le moteur.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              Pour mettre en place ces mécanismes dès cette semaine, notre guide sur les 7 actions concrètes de fidélisation à mettre en place dès cette semaine vous donne le plan étape par étape.
            </p>

            <img
              src={parrainageImage}
              alt="Processus de parrainage client avec QR code wallet partage ami inscription et récompense pour les deux utilisateurs"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Section 5 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Mesurer et entretenir ses ambassadeurs dans la durée
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un ambassadeur qui se sent oublié redevient passif. La relation avec vos clients les plus engagés ne se gère pas de façon automatique : elle se cultive.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Trois indicateurs suffisent pour suivre l'activité de votre base d'ambassadeurs : le nombre de parrainages actifs sur les 30 derniers jours, le taux de conversion des filleuls (ceux qui ont effectué un achat après inscription), et la fréquence de visite des ambassadeurs comparée au reste de la base.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'animation spécifique ambassadeur : 1 communication exclusive par trimestre, distincte de votre calendrier d'animation standard. Une avant-première, un avantage inédit, ou simplement une reconnaissance formelle de leur rôle. "Vous faites partie de nos 30 clients fondateurs" crée plus d'engagement qu'une remise de 10 %.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              La reconnaissance du statut d'ambassadeur est elle-même un levier d'attachement. Un client qui sait qu'il est reconnu comme ambassadeur défend votre marque autrement. Il ne recommande plus par réflexe : il recommande parce qu'il considère que faire partie de votre communauté est un avantage qu'il veut faire connaître.
            </p>

            <img
              src={kpiImage}
              alt="Dashboard de suivi des ambassadeurs avec parrainages mensuels conversion des filleuls et engagement client comparé"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Conclusion */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Conclusion
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un <strong className="font-semibold">client ambassadeur</strong> ne se crée pas par accident. Il naît d'une expérience qui dépasse les attentes, d'un sentiment de reconnaissance maintenu dans le temps, et d'un mécanisme simple qui lui permet d'agir. Les structures qui cultivent ces conditions voient leur base croître sans augmenter leur budget acquisition. Unotif permet de créer un programme de parrainage et d'animer vos clients ambassadeurs depuis une seule interface.
            </p>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ClientAmbassadeur;
